import { useMemo, useState } from 'react'
import { TEST_CATALOG } from '../../constants'
import type { OrderMessageTone, TestCatalogItem, TestCategory, TestSortMode } from './types'

interface UseTestsTabStateResult {
  category: TestCategory
  setCategory: (category: TestCategory) => void
  searchQuery: string
  setSearchQuery: (query: string) => void
  sortMode: TestSortMode
  setSortMode: (mode: TestSortMode) => void
  popularOnly: boolean
  togglePopularOnly: () => void
  hasActiveFilters: boolean
  resetFilters: () => void
  filtered: TestCatalogItem[]
  orderedTestIds: Set<string>
  orderMessage: string | null
  orderMessageTone: OrderMessageTone
  orderingTestId: string | null
  handleOrderTest: (testId: string) => Promise<void>
}

export function useTestsTabState(): UseTestsTabStateResult {
  const [category, setCategory] = useState<TestCategory>('All')
  const [searchQuery, setSearchQuery] = useState('')
  const [sortMode, setSortMode] = useState<TestSortMode>('recommended')
  const [popularOnly, setPopularOnly] = useState(false)
  const [orderedTestIds, setOrderedTestIds] = useState<Set<string>>(() => new Set())
  const [orderMessage, setOrderMessage] = useState<string | null>(null)
  const [orderingTestId, setOrderingTestId] = useState<string | null>(null)

  const filtered = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase()
    let catalog = category === 'All'
      ? [...TEST_CATALOG]
      : TEST_CATALOG.filter((test) => test.category === category)

    if (popularOnly) {
      catalog = catalog.filter((test) => test.popular)
    }

    if (normalizedQuery) {
      catalog = catalog.filter((test) => {
        const haystack = `${test.name} ${test.description} ${test.partner} ${test.category}`.toLowerCase()
        return haystack.includes(normalizedQuery)
      })
    }

    if (sortMode === 'price-low') {
      catalog.sort((a, b) => a.price - b.price)
    } else if (sortMode === 'price-high') {
      catalog.sort((a, b) => b.price - a.price)
    } else if (sortMode === 'name-a-z') {
      catalog.sort((a, b) => a.name.localeCompare(b.name))
    }

    return catalog
  }, [category, popularOnly, searchQuery, sortMode])

  const orderMessageTone: OrderMessageTone = orderMessage?.startsWith('[') ? 'danger' : 'success'
  const hasActiveFilters = category !== 'All' || popularOnly || searchQuery.trim().length > 0 || sortMode !== 'recommended'

  function togglePopularOnly() {
    setPopularOnly((current) => !current)
  }

  function resetFilters() {
    setCategory('All')
    setSearchQuery('')
    setSortMode('recommended')
    setPopularOnly(false)
  }

  async function handleOrderTest(testId: string): Promise<void> {
    if (orderedTestIds.has(testId) || orderingTestId) {
      return
    }

    const selectedTest = TEST_CATALOG.find((test) => test.id === testId)
    if (!selectedTest) {
      setOrderMessage('[TEST_NOT_FOUND] Unable to order unknown test.')
      return
    }

    setOrderMessage(null)
    setOrderingTestId(testId)

    await new Promise((resolve) => {
      window.setTimeout(resolve, 350)
    })

    setOrderedTestIds((current) => new Set(current).add(testId))
    setOrderingTestId(null)
    setOrderMessage(`Ordered "${selectedTest.name}". A confirmation has been added to your queue.`)
  }

  return {
    category,
    setCategory,
    searchQuery,
    setSearchQuery,
    sortMode,
    setSortMode,
    popularOnly,
    togglePopularOnly,
    hasActiveFilters,
    resetFilters,
    filtered,
    orderedTestIds,
    orderMessage,
    orderMessageTone,
    orderingTestId,
    handleOrderTest,
  }
}
